<template>
  <div class="container-fluid invoices-page">
    <div class="row">
      <div class="col-12">
        <div class="page-table-header full-height-page-wrapper">
          <PageHeader showBreadcrumbs :page-title="'Invoices'">
            <template #buttons>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowBulkCreation ? 'secondary' : 'info'"
                @click="isShowBulkCreation = !isShowBulkCreation"
              >
                <span class="material-icons icon-image-preview"> tune </span>
                Bulk Creation
              </b-button>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview"> tune </span>
                Filters
              </b-button>
              <b-button
                variant="success"
                v-if="$can(permissions.CreateFinancesInvoices)"
                type="button"
                class="button box-shadow-onfocus-none"
                @click.prevent="showCreateInvoices"
                :disabled="isDataPageLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            ref="filtersComponent"
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            resetToInitial
            :data="filters"
            :isLoading="isDataPageLoading && isInitialDataLoaded"
            @submit="setFilters"
          />

          <InvoicesBulkCreation
            ref="invoice_bulk_creation"
            v-show="isShowBulkCreation"
            @updateInvoices="updateInvoices()"
          />

          <div
            v-if="isDataPageLoading && !isInitialDataLoaded"
            class="content-loading-wrapper"
          >
            <Preloader class="centered-preloader" />
          </div>

          <div v-else class="full-height-content">

            <div v-if="getInvoicesTableData.length">
              <TableForPage
                ref="table"
                :tableData="getInvoicesTableData"
                :isDataLoading="isDataPageLoading && isInitialDataLoaded"
                :fields="table.fields"
                :perPage="getInvoices.per_page"
                :currentPage="getInvoices.current_page"
                :totalRows="getInvoices.total"
                :slots="table.slots"
                :tableRowModalFields="tableRowModal.fields"
                isServerRender
                noLocalSorting
                @update="fetchInvoices($event)"
              >
                <template v-slot:rowModal.client_name="slotProps">
                  <td class="entities-table-row-modal__label">Client</td>
                  <td>
                    {{ getFullName(slotProps.field) }}
                  </td>
                </template>
                <template v-slot:rowModal.periods="slotProps">
                  <template v-if="slotProps.field.length">
                    <td class="entities-table-row-modal__label">Periods</td>
                    <td>
                      <div>
                        <div
                          class="entities-table-row-modal__period"
                          v-for="(period, index) in slotProps.field"
                          :key="index"
                        >
                          <div>Date from: {{ period.date_from }}</div>
                          <div>Date to: {{ period.date_to }}</div>
                          <div>Hours amount: {{ period.hours_amount }}</div>
                          <div>Total amount: {{ period.total_amount }}</div>
                          <div>
                            Invoice period type:
                            {{ period.invoice_period_type.name }}
                          </div>
                          <div>
                            Period cost EUR: {{ period.period_costEUR }}
                          </div>
                          <div>
                            Period cost UAH: {{ period.period_costUAH }}
                          </div>
                          <div>
                            Period cost USD: {{ period.period_costUSD }}
                          </div>
                        </div>
                      </div>
                    </td>
                  </template>
                </template>
                <template v-slot:rowModal.items="slotProps">
                  <template v-if="slotProps.field.length">
                    <td class="entities-table-row-modal__label">Items</td>
                    <td>
                      <div>
                        <div
                          class="entities-table-row-modal__items"
                          v-for="(period, index) in slotProps.field"
                          :key="index"
                        >
                          <div>Name: {{ period.type_display_name }}</div>
                          <div>Hour Cost: {{ period.pivot.hour_cost }}</div>
                          <div>
                            Hours Amount: {{ period.pivot.hours_amount }}
                          </div>
                        </div>
                      </div>
                    </td>
                  </template>
                </template>
                <template v-slot:invoice_number="slotProps">
                  <a href="#" @click.prevent="showViewModal(slotProps)">{{
                    slotProps.item.invoice_number
                  }}</a>
                </template>
                <template v-slot:invoice_status="slotProps">
                  <div class="d-flex align-items-center gap-1">
                    <PayoutStatus :status="slotProps.item.invoice_status" />
                    <span
                      v-if="isInvoiceOverdue(slotProps.item)"
                      class="material-symbols-outlined ms-2 text-danger"
                      style="font-size: 20px; cursor: help;"
                      v-b-tooltip.hover="{
                        title: `This invoice is overdue by ${getOverdueDays(slotProps.item)} days.<br>Due date: ${formatDate(slotProps.item.due_date)}`,
                        variant: 'base',
                        container: 'body',
                        placement: 'top',
                        boundary: 'viewport',
                        html: true,
                        delay: 300
                      }"
                    >
                      event_busy
                    </span>
                  </div>
                </template>
                <template v-slot:total_amount="slotProps">
                  {{ slotProps.item.total_amount }}
                  {{ getCurrencyCode(slotProps.item) }}
                </template>
                <template v-slot:col_actions="slotProps">
                  <InvoicesLinks
                    :templates="getTemplates"
                    :item="slotProps.item"
                    isRouteUpdate
                    @reloadInvoices="updateInvoices"
                    @showEditForm="
                      ($event) => {
                        showEditInvoice($event, slotProps.item);
                        scrollToTheTop();
                      }
                    "
                  />
                </template>
              </TableForPage>

              <div>
                <div class="mt-20px">
                  <b-card-group deck class="mb-3">
                    <MoneyTotalsCard
                      title="Original totals"
                      :data="totalAmount.original"
                    />
                    <MoneyTotalsCard
                      title="Equivalent totals"
                      :data="totalAmount.equivalent"
                    />
                  </b-card-group>
                </div>
              </div>
            </div>

            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && !getInvoicesTableData.length && !getInvoices.isAnyData"
              customIcon="folder_off"
              customIconClasses="material-symbols-outlined empty-state__icon"
              title="No invoices yet"
              description="You haven't issued any invoices yet. Create your first invoice to start billing."
              buttonText="CREATE INVOICES"
              @button-click="handleCreateClick"
            />
            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && !getInvoicesTableData.length && getInvoices.isAnyData"
              @reset-filters="handleResetFilters"
            />
          </div>
        </div>

        <FinanceInvoicesForm
          ref="financeInvoicesForm"
          v-model="invoiceFormData.show"
          :mode="invoiceFormData.mode"
          :data="invoiceFormData.form"
          :title="invoiceFormData.title"
          :headerTitle="invoiceFormData.headerTitle"
          :submitButtonText="invoiceFormData.submitButtonText"
          :bankDetails="getOwnBankDetails"
          :templates="getTemplates"
          :projects="getActualProjects"
          :statuses="getStatuses"
          :types="getInvoicesTypes"
          :isLoading="getIsModalLoading"
          @submit="saveFinanceInvoices"
          @invoiceFormClosed="closeInvoiceForm"
          @showDownloadModal="showDownloadInvoiceModal"
          :breadcrumbs="invoiceFormBreadcrumbs"
        />

        <!-- Download Invoice Modal -->
        <DownloadInvoiceModal
          v-model="downloadInvoiceModal.show"
          :invoice="downloadInvoiceModal.invoice"
          :templates="getTemplates"
          :triggerElement="downloadInvoiceModal.triggerElement"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Moment from 'moment'
import { permissions } from '@mocks/permissions'
import { PostalNames } from '@/constants/PortalNames'
import Preloader from '@components/Preloader'
import TableForPage from '@tables/TableForPage'
import FinanceInvoicesForm from '@components/modals/FinanceInvoicesForm/FinanceInvoicesForm.vue'
import Filters from '@components/Filter/Filter'
import PayoutStatus from '@components/tables/elements/PayoutStatus'
import PageHeader from '@components/PageHeader'
import InvoiceFormModal from '../mixins/InvoiceFormModal'
import InvoicesLinks from '../components/InvoicesLinks'
import InvoicesBulkCreation from '@components/InvoicesBulkCreation.vue'
import MoneyTotalsCard from '@components/MoneyTotalsCard'
import ScrollPositionMixin from '@mixins/ScrollPositionMixin'
import EmptyStateNoResults from '@components/EmptyStateNoResults'
import DownloadInvoiceModal from '@components/modals/DownloadInvoiceModal.vue'

export default {
  name: 'FinanceInvoices',
  mixins: [InvoiceFormModal, ScrollPositionMixin],
  components: {
    Preloader,
    InvoicesLinks,
    TableForPage,
    FinanceInvoicesForm,
    Filters,
    PayoutStatus,
    PageHeader,
    InvoicesBulkCreation,
    MoneyTotalsCard,
    EmptyStateNoResults,
    DownloadInvoiceModal
  },
  data () {
    const className = 'invoices-filters-field-wrapper'

    return {
      permissions: permissions,
      isShowFilter: true,
      isShowBulkCreation: false,
      modalLoading: false,
      isAllOptionsFilterSet: false,
      isInvoicesDataLoaded: false,
      downloadInvoiceModal: {
        show: false,
        invoice: null,
        triggerElement: null
      },
      filters: {
        invoice_number: {
          type: 'input',
          name: 'invoice_number',
          label: 'Invoice number',
          placeholder: 'Invoice number',
          class: className
        },
        project: {
          type: 'select',
          name: 'project_id',
          label: 'Project',
          selectLabel: 'label',
          valueField: 'id',
          options: [],
          class: className
        },
        date_from: {
          type: 'datepicker',
          name: 'date_from',
          label: 'Date from',
          format: 'dd/MM/yyyy',
          value: Moment().startOf('month').toISOString(),
          defaultValue: Moment().startOf('month').toISOString(),
          class: className
        },
        date_to: {
          type: 'datepicker',
          name: 'date_to',
          label: 'Date to',
          format: 'dd/MM/yyyy',
          value: Moment().endOf('month').toISOString(),
          defaultValue: Moment().endOf('month').toISOString(),
          class: className
        },
        bank_details: {
          type: 'multiselect',
          name: 'own_bank_details_id',
          nameItem: 'own_bank_details_id',
          label: 'Own bank details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: [],
          class: className
        },
        client_details: {
          type: 'multiselect',
          name: 'client_bank_details_id',
          nameItem: 'client_bank_details_id',
          label: 'Client bank details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: [],
          class: className
        },
        invoice_type: {
          type: 'select',
          name: 'invoice_type_id',
          label: 'Invoice type',
          selectLabel: 'type',
          valueField: 'id',
          options: [],
          class: className
        },
        invoice_status: {
          type: 'select',
          name: 'invoice_status_id',
          label: 'Invoice status',
          selectLabel: 'display_name',
          valueField: 'id',
          options: [],
          class: className
        }
      },
      table: {
        slots: {
          invoice_status: {
            name: 'invoice_status'
          },
          total_amount: {
            name: 'total_amount'
          },
          col_actions: {
            name: 'col_actions'
          }
        },
        fields: [
          {
            key: 'project.name',
            label: 'Project name'
          },
          {
            key: 'invoice_status',
            label: 'Status'
          },
          {
            key: 'issue_date',
            label: 'Issue date',
            sortable: true
          },
          {
            key: 'due_date',
            label: 'Due date',
            sortable: true
          },
          {
            key: 'paid_date',
            label: 'Paid date',
            sortable: true
          },
          {
            key: 'total_amount',
            label: 'Amount'
          },
          {
            key: 'col_actions',
            label: 'Actions',
            class: 'actions-column text-left'
          }
        ],
        params: {
          filters: null,
          sorting: null,
          currentPage: 1
        }
      },
      tableRowModal: {
        fields: [
          {
            key: 'own_bank_details.summary',
            label: 'Own bank details'
          },
          {
            key: 'project.name',
            label: 'Project name'
          },
          {
            key: 'client_bank_details.summary',
            label: 'Client bank details'
          },
          {
            key: 'invoice_status.display_name',
            label: 'Invoice status'
          },
          {
            key: 'invoice_template_type.name',
            label: 'Invoice template type'
          },
          {
            key: 'comment',
            label: 'Comment'
          },
          {
            key: 'comment_national',
            label: 'Comment national'
          },
          {
            key: 'issue_date',
            label: 'Issue date'
          },
          {
            key: 'due_date',
            label: 'Due date'
          },
          {
            key: 'invoice_type.type',
            label: 'Invoice type'
          },
          {
            key: 'client_bank_details',
            type: 'slot',
            label: 'Client name',
            slotName: 'client_name'
          },
          {
            key: 'total_amount',
            label: 'Total amount'
          },
          {
            key: 'periods',
            type: 'slot',
            label: 'Periods',
            slotName: 'periods'
          },
          {
            key: 'items',
            type: 'slot',
            label: 'Items',
            slotName: 'items'
          },
          {
            key: 'specialisations',
            type: 'slot',
            label: 'Specialisations',
            slotName: 'specialisations'
          }
        ]
      },
      PostalNames
    }
  },
  created () {
    this.isDataPageLoading = true
    this.fetchInvoiceFormData()
      .then(() => {
        this.setAllOptionsFilter()
        this.setFilters(this.filters)
      })
  },
  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isInvoicesDataLoaded
    },
    getInvoicesTableData () {
      if (!this.getInvoices.data) return []
      return this.getInvoices.data.map((item) => {
        return {
          ...item,
          due_date: this._toLocalTime(item.due_date),
          paid_date: this._toLocalTime(item.paid_date),
          issue_date: this._toLocalTime(item.issue_date)
        }
      })
    },
    totalAmount () {
      return this.getInvoices.totalAmount
    },
    invoiceFormBreadcrumbs () {
      return [
        {
          text: 'Home',
          to: { name: 'home' }
        },
        {
          text: 'Invoices',
          click: this.hideInvoiceForm
        }
      ]
    }
  },
  methods: {
    toggleShowFilter () {
      this.isShowFilter = !this.isShowFilter
    },
    getFullName (data) {
      if (data) {
        return `${data.first_name} ${data.last_name}`
      }
    },
    getCurrencyCode (data) {
      if (data.client_bank_details) {
        return data.client_bank_details.currency.code
      }
      if (data.own_bank_details) {
        return data.own_bank_details.currency.code
      }
    },
    showViewModal (data) {
      this.$refs['table'].showView(
        data,
        `Invoice number ${data.item.invoice_number}`
      )
    },
    showDownloadInvoiceModal (data) {
      this.downloadInvoiceModal.invoice = data.invoice
      this.downloadInvoiceModal.triggerElement = data.triggerElement
      this.downloadInvoiceModal.show = true
    },
    async setFilters (data) {
      this.setParamsFilters(data)
      this.setParamsCurrentPage()
      await this.fetchInvoices()
    },
    setParamsFilters (data) {
      const that = this
      const filters = Object.values(data).reduce(function (state, current) {
        let value
        if (current['value']) {
          if (current['type'] === 'datepicker') {
            value = that._toLocalTime(current['value'])
          } else if (Array.isArray(current['value'])) {
            value = JSON.stringify(
              current['value'].map((item) => {
                return item.id
              })
            )
          } else {
            value =
              typeof current['value'] === 'object'
                ? current['value'][current['valueField']]
                : current['value']
          }

          if (value) {
            state += `&filter[${current.name}]=${value}`
          }
        }
        return state
      }, '')

      this.table.params.filters = filters || null
    },
    setParamsSort (sortDesc, sortBy) {
      if (sortBy) {
        const sortDirection = sortDesc ? 'desc' : 'asc'
        this.table.params.sorting = `&sort[${sortBy}]=${sortDirection}`
      }
    },
    setParamsCurrentPage (currentPage = 1) {
      this.table.params.currentPage = currentPage
    },
    updateInvoices (data) {
      if (!data) return this.fetchInvoices()

      const { currentPage, sortDesc, sortBy } = data

      this.setParamsCurrentPage(currentPage)
      this.setParamsSort(sortDesc, sortBy)

      this.fetchInvoices()
    },
    setAllOptionsFilter () {
      this.filters.bank_details.options = this.getOwnBankDetails
      this.filters.client_details.options = this.getClientsBankDetailsAll
      this.filters.invoice_status.options = this.getStatuses
      this.filters.project.options = this.getProjects
      this.filters.invoice_type.options = this.getInvoicesTypes

      this.isAllOptionsFilterSet = true
    },
    async fetchInvoices (tableParams = null) {
      if (tableParams) {
        this.table.params.currentPage = tableParams.currentPage

        if (tableParams.sortBy) {
          this.table.params.sorting =
            '&sort[' +
            tableParams.sortBy +
            ']=' +
            (tableParams.sortDesc ? 'desc' : 'asc')
        }
      }

      let params = '?page=' + this.table.params.currentPage

      if (this.table.params.sorting) {
        params += this.table.params.sorting
      }
      if (this.table.params.filters) {
        params += this.table.params.filters
      }

      return this._toggleDataPageLoading(() =>
        this.$api.finances.getInvoices(params)
      ).then((result) => {
        this.isInvoicesDataLoaded = true
        return result
      })
    },
    async saveFinanceInvoices (fields) {
      await this.saveInvoice(fields)
      // Clear backend validation errors after successful submission
      if (this.$refs.financeInvoicesForm) {
        this.$refs.financeInvoicesForm.clearAllBackendErrors()
      }
      this.closeInvoiceForm()
      this.updateInvoices()
    },
    closeInvoiceForm () {
      const route = this.$router.resolve({ name: 'finances-invoices' })
      this.restoreScrollPosition()
      window.history.pushState({}, '', route.href)
    },
    handleResetFilters () {
      if (this.$refs['filtersComponent']) {
        this.$refs['filtersComponent'].resetFilter()
      }
    },
    handleCreateClick () {
      this.showCreateInvoices()
    },
    handleBackendValidationErrors (error) {
      // Pass the backend errors to the form component
      if (error?.response?.data?.errors && this.$refs.financeInvoicesForm) {
        this.$refs.financeInvoicesForm.backendErrors = error.response.data.errors
        // Focus the first field with backend validation errors
        this.$nextTick(() => {
          this.$refs.financeInvoicesForm.focusFirstErrorFieldFromBackend()
        })
      }
    },
    errorNotifications (error) {
      // Handle validation errors with a single message instead of detailed errors
      if (error?.response?.data?.errors) {
        this.$snotify.error(
          'The form is not valid. Please fill all required fields with valid data.',
          'Validation Error',
          this.$store.state.snotify
        )
      } else {
        // For non-validation errors, use the default error handling
        // Call the parent Global mixin method
        this.$options.mixins
          .find(mixin => mixin.methods && mixin.methods.errorNotifications)
          .methods.errorNotifications.call(this, error)
      }
    },
    formatDate (date) {
      if (!date) return ''
      return Moment(date).format('DD.MM.YYYY')
    },
    isInvoiceOverdue (invoice) {
      if (!invoice.due_date || !invoice.invoice_status) {
        return false
      }

      const isPaid = invoice.invoice_status.name === 'paid'
      if (isPaid) {
        return false
      }

      const today = new Date()
      const dueDate = new Date(invoice.due_date)
      today.setHours(0, 0, 0, 0)
      dueDate.setHours(0, 0, 0, 0)

      return dueDate < today
    },
    getOverdueDays (invoice) {
      if (!invoice.due_date) return 0

      const today = new Date()
      const dueDate = new Date(invoice.due_date)
      today.setHours(0, 0, 0, 0)
      dueDate.setHours(0, 0, 0, 0)

      const diffTime = today - dueDate
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      return diffDays > 0 ? diffDays : 0
    }
  }
}
</script>

<style lang="scss" scoped>
.invoices-page {
  ::v-deep th.actions-column {
    padding-left: 24px;
  }
}

.invoices-page :global(.table__theme-default table td) {
  padding-top: 8px;
  padding-bottom: 8px;
}

.entities-table-row-modal__period,
.entities-table-row-modal__items {
  padding: 10px;
  margin-bottom: 15px;
  background-color: #eaeaea;
  &:last-child {
    margin-bottom: 0;
  }
}

.material-symbols-outlined.text-danger {
  color: #dc3545 !important;
}
</style>
